// GSTIN and PAN checks that need no network: format, GSTIN check digit, the PAN
// embedded in a GSTIN, and the GSTIN state code against the address state.
// These catch typos and mismatched numbers; they do not prove the number is
// registered. Only the GST/Income Tax portals can do that.

const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const CHARS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';

export const normalizeTaxId = (v: string | undefined) => (v ?? '').replace(/\s+/g, '').toUpperCase();

export const isPan = (v: string) => PAN_RE.test(v);

/** The 15th GSTIN character for the first 14 (mod-36 weighted sum). */
export function gstinCheckChar(first14: string): string {
  let sum = 0;
  for (let i = 0; i < 14; i++) {
    const p = CHARS.indexOf(first14[i]!) * (i % 2 === 0 ? 1 : 2);
    sum += Math.floor(p / 36) + (p % 36);
  }
  return CHARS[(36 - (sum % 36)) % 36]!;
}

export const isGstin = (v: string) => GSTIN_RE.test(v) && gstinCheckChar(v.slice(0, 14)) === v[14];

/** GST state codes for each state or union territory name used in address forms. */
const STATE_CODES: Record<string, string[]> = {
  'Jammu and Kashmir': ['01'], 'Himachal Pradesh': ['02'], Punjab: ['03'], Chandigarh: ['04'], Uttarakhand: ['05'], Haryana: ['06'], Delhi: ['07'],
  Rajasthan: ['08'], 'Uttar Pradesh': ['09'], Bihar: ['10'], Sikkim: ['11'], 'Arunachal Pradesh': ['12'], Nagaland: ['13'], Manipur: ['14'],
  Mizoram: ['15'], Tripura: ['16'], Meghalaya: ['17'], Assam: ['18'], 'West Bengal': ['19'], Jharkhand: ['20'], Odisha: ['21'], Chhattisgarh: ['22'],
  'Madhya Pradesh': ['23'], Gujarat: ['24'], 'Dadra and Nagar Haveli and Daman and Diu': ['25', '26'], Maharashtra: ['27'], 'Andhra Pradesh': ['28', '37'],
  Karnataka: ['29'], Goa: ['30'], Lakshadweep: ['31'], Kerala: ['32'], 'Tamil Nadu': ['33'], Puducherry: ['34'], 'Andaman and Nicobar Islands': ['35'],
  Telangana: ['36'], Ladakh: ['38'],
};

export interface TaxIdInput { gstin?: string; pan?: string; state?: string }

/**
 * Returns a message for the first problem, or null. PAN is always required;
 * GSTIN is required when `gstinRequired` (pumps). Unknown state text is not
 * compared, so older free-text addresses are not rejected.
 */
export function checkTaxIds(input: TaxIdInput, opts: { gstinRequired?: boolean } = {}): string | null {
  const gstin = normalizeTaxId(input.gstin);
  const pan = normalizeTaxId(input.pan);
  if (!pan) return 'Enter the business PAN.';
  if (!isPan(pan)) return 'PAN looks wrong. It is 10 characters like ABCDE1234F.';
  if (!gstin) return opts.gstinRequired ? 'Enter the GSTIN.' : null;
  if (!GSTIN_RE.test(gstin)) return 'GSTIN looks wrong. It is 15 characters like 27ABCDE1234F1Z5.';
  if (gstinCheckChar(gstin.slice(0, 14)) !== gstin[14]) return 'GSTIN check digit does not match. Check for a typing mistake.';
  if (gstin.slice(2, 12) !== pan) return 'The PAN inside this GSTIN does not match the PAN you entered.';
  const codes = input.state ? STATE_CODES[input.state] : undefined;
  if (codes && !codes.includes(gstin.slice(0, 2))) return `GSTIN starts with state code ${gstin.slice(0, 2)}, which is not ${input.state}.`;
  return null;
}
